import { createContext, useState, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useParams, useRouteLoaderData } from 'react-router-dom';
import { useGlobalState, setSavePopup, getSavePopup } from "../configurator/State";
import FullPrintSummary from '../configurator/FullPrintSummary';
import bmkservice from '../service/bmk-service';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logoutUser } from '../store/slices/authSlice';
import { graphqlRequest } from '../service/graphql-client';
import { GQ_ME_QUERY, GQ_SAVE_BUILD } from '../service/graphql-mutations';

// Import modal components
import Modal from '../components/modals/Modal';
import LoginModal from '../components/modals/LoginModal';
import SignupModal from '../components/modals/SignupModal';
import ProfileModal from '../components/modals/ProfileModal';
import ForgotPasswordModal from '../components/modals/ForgotPasswordModal';
import SaveBuildModal from '../components/modals/SaveBuildModal';
import LoadBuildModal from '../components/modals/LoadBuildModal';
import ShareModal from '../components/modals/ShareModal';
import ShareByEmailModal from '../components/modals/ShareByEmailModal';
import DisclaimerModal from '../components/modals/DisclaimerModal';
import ErrorModal from '../components/modals/ErrorModal';
import LoadingModal from '../components/modals/LoadingModal';
import SuccessModal from '../components/modals/SuccessModal';
import ChangePasswordModal from '../components/modals/ChangePasswordModal';

export const ModalContext = createContext();

export default function ModalProvider({ children }) {
    const dispatch = useAppDispatch();
    const { accessToken } = useAppSelector((state) => state.auth);
    const [modalType, setModalType] = useState(null);
    const [modalProps, setModalProps] = useState({});
    const productDetails = useRouteLoaderData("productDetails");
    const [user] = useGlobalState('User');
    const { productSlug, seriesSlug, modelName } = useParams();

    const [addOns] = useGlobalState("AddOns");
    const [prices] = useGlobalState("Prices");
    const [kmaintenance] = useGlobalState("KMaintenance");
    const [warranty] = useGlobalState("Warranty");
    const [insurance] = useGlobalState("Insurance");
    const [ZipCode] = useGlobalState("ZipCode");

    const fullProductURL = window.location.protocol + "//" + window.location.host + '/' +
        encodeURIComponent(productSlug) + '/' + encodeURIComponent(seriesSlug) + '/' + encodeURIComponent(modelName);

    const closeModal = () => {
        setModalType(null);
        setModalProps({});
    };

    const show = (content) => {
        if (!content) {
            // If no content provided, close the modal
            closeModal();
            return;
        }
        // If content is a string, treat it as a loading message
        if (typeof content === 'string') {
            setModalType('loading');
            setModalProps({ message: content });
        } else {
            // Otherwise, treat it as custom JSX content
            setModalType('custom');
            setModalProps({ content });
        }
    };

    const showDisclaimer = (index) => {
        setModalType('disclaimer');
        setModalProps({ disclaimerIndex: index });
    };

    const trySave = () => {
        if (!productDetails || !productDetails.Tractor || !productDetails.Tractor.Material || productDetails.Tractor.Material.length <= 0) {
            setModalType('error');
            setModalProps({ 
                title: 'Finish Building',
                message: 'Please select and get started on your custom build out your Dream Vehicle before trying to Save.'
            });
        } else {
            showLogin("Log In to Save Your Build", () => showSave());
        }
    };
    const showLogin = async (promptMessage, successHandler) => {
        if (accessToken) {
            try {
                const data = await graphqlRequest(GQ_ME_QUERY, {}, accessToken);

                if (data.me) {
                    successHandler(); // Call the success handler directly
                    return;
                }
            } catch (error) {
                // Token is invalid, continue to show login modal
                console.log('Token validation failed, showing login modal');
            }
        }

        // If no token or token is invalid, show login modal
        setModalType('login');
        setModalProps({ 
            promptMessage, 
            onSuccess: () => {
                // After successful login, show profile first
                showProfile();
            },
            onShowSignup: () => showSignup(() => {
                // After successful signup, show profile first
                showProfile();
            }),
            onShowForgotPassword: () => showForgotPassword()
        });
    };

    const showSignup = (successHandler) => {
        setModalType('signup');
        setModalProps({
            onSuccess: successHandler,
            onShowLogin: () => showLogin("Log In to Save Your Build", successHandler)
        });
    };

    const showProfile = () => {
        setModalType('profile');
        setModalProps({
            onShowSavedBuilds: () => showLoad(),
            onSaveBuild: () => showSave(),
            onChangePassword: () => showChangePassword(),
            onSignOut: () => handleLogout()
        });
    };

    const showChangePassword = () => {
        setModalType('changePassword');
        setModalProps({
            onClose: () => showProfile()
        });
    };

    const showForgotPassword = () => {
        setModalType('forgotPassword');
        setModalProps({
            onClose: () => showLogin("Log In to Save Your Build"),
            onShowLogin: () => showLogin("Log In to Save Your Build")
        });
    };

    const handleLogout = async () => {
        try {
            await dispatch(logoutUser());
            // Just close the modal after logout
            closeModal();
        } catch (error) {
            console.error('Logout error:', error);
            // Even if logout fails, close modal
            closeModal();
        }
    };

    const showSave = () => {
        setModalType('saveBuild');
        setModalProps({
            onSave: saveAndConfirmBuild,
            productDetails
        });
    };

    const tryLoad = () => {
        showLogin("Log In to View Your Builds", () => showLoad());
    };

    const showLoad = () => {
        setModalType('loadBuild');
        setModalProps({
            onShowProfile: () => showProfile()
        });
    };

    useEffect(() => {
        document.addEventListener('mouseout', e => {
            if (!e.toElement && !e.relatedTarget && getSavePopup()) {
                // trySave();
                setSavePopup(false);
            }
        });
    }, []);

    // Modal handlers for share functionality
    const showShare = async () => {
        setModalType('loading');
        setModalProps({ message: 'Loading...' });

        const savedBuild = await saveBuild();
        const shareURL = savedBuild.productURL + '?build-id=' + savedBuild.id;

        window.gtag('event', 'Share', {
            'Model': productDetails.Tractor.Name
        });

        setModalType('share');
        setModalProps({
            savedBuild,
            productDetails,
            onShareByEmail: () => showShareByEmail(),
            onCopyLink: () => navigator.clipboard.writeText(shareURL)
        });
    };

    const showShareByEmail = () => {
        setModalType('shareByEmail');
        setModalProps({
            onSubmit: async (email) => {
                await sendToIKQ(2, { email: email });
            }
        });
    };

    // Essential functions for saving and sharing builds

    const saveAndConfirmBuild = async (saveBuildName) => {
        setModalType('loading');
        setModalProps({ message: 'Saving...' });

        await saveBuild(saveBuildName);

        window.gtag('event', 'Save', {
            'Model': productDetails.Tractor.Name
        });

        setModalType('success');
        setModalProps({ 
            title: 'Build Saved Successfully',
            message: 'Your Build Has Been Successfully Saved!'
        });
    };

    const saveBuild = async (saveBuildName) => {
        setSavePopup(false);

        if (!saveBuildName || saveBuildName.length <= 0)
            saveBuildName = 'My Dream ' + productDetails.Tractor.Material;

        // Prepare configured items - ensure it's a JSON string
        let configuredItems = productDetails.configuration.configuredItems;
        if (typeof configuredItems !== 'string') {
            configuredItems = JSON.stringify(configuredItems);
        }

        // Find matching image
        const modelSets = [productDetails.Tractor.Modelset];
        const defaultView = Object.keys(productDetails.studios)[0];
        Object.values(productDetails.configuration.configuredItems).forEach((item) => {
            if (item.Modelset) {
                modelSets.push(item.Modelset);
            }
        });
        const matchingImage = productDetails.imageMappingData.find((imd) => {
            if (!productDetails.studios[defaultView] || imd.studio !== productDetails.studios[defaultView].id) {
                return false;
            }
            if (imd.modelsets.length !== modelSets.length) {
                return false;
            }
            if (!imd.modelsets.every((ms) => modelSets.includes(ms))) {
                return false;
            }
            return true;
        });
        
        let imageUrl = '';
        if (matchingImage) {
            imageUrl = productDetails.baseUrl + 'images/' + encodeURIComponent(matchingImage.image);
        }
        else {
            imageUrl = productDetails.Tractor.ImagePath;
        }

        // Use GraphQL mutation to save build
        try {
            const data = await graphqlRequest(GQ_SAVE_BUILD, {
                productURL: fullProductURL,
                savedURL: window.location.href,
                configuredItems: configuredItems,
                buildName: saveBuildName,
                uc: user || '',
                image: imageUrl,
                un: user || undefined
            }, accessToken);

            if (data.saveBuild && data.saveBuild.build) {
                return data.saveBuild.build;
            } else {
                throw new Error('Failed to save build');
            }
        } catch (error) {
            console.error('Error saving build:', error);
            throw error;
        }
    }

    async function sendToIKQ(actionType = 1, customerData = {}) {
        let quote = productDetails.configuration.createQuoteRequest();
        quote.customer = {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            comments: '',
            zip: ZipCode,
            selectedDealer: '',
            overSixteen: 'True',
            optInMarketing: 'False',

            ...customerData
        };
        quote.userAction = actionType;
        quote.ipAddress = '127.0.0.1';

        quote.cashCIRAmount = prices.totalPrice;
        quote.financeCIRAmount = prices.monthlyPayment;
        quote.orangePlusAmount = 0;
        quote.interestInKtac = insurance || false;
        quote.interestInKMaintenance = kmaintenance || false;
        quote.interestInWarranty = warranty || false;
        quote.selectedPackageId = productDetails.configuration.package?.id;

        // update price to the quote.tractor object with total price, This total price will show in quote email
        // quote email shows the price with tractor price + options -> price
        // so we need to subtract the options price from the total price
        if (quote.tractor) {
            let totalPrice = prices.totalPrice;
            if (quote.options && Array.isArray(quote.options)) {
                quote.options.forEach(opt => {
                    if (opt && typeof opt.price === 'number') {
                        totalPrice -= opt.price;
                    }
                });
            }
            quote.tractor.price = totalPrice;
        }

        let response = await fetch(bmkservice.IKQSaveData,
            {
                method: "POST",
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(quote)
            }
        );
        let json = await response.json();

        if (!json || !json.BMKQuoteId) {
            return false;
        }

        let htmlContents = renderToStaticMarkup(
            <FullPrintSummary product={productDetails} prices={prices} selectedAddOns={addOns} quote={quote} quoteid={json.BMKQuoteId} printContents="detailed" />
        );

        // Create a temporary container to render the HTML
        // Use 1600px width with scale 1 (equivalent to 800px × scale 2 for quality)
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = htmlContents;
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.width = '1000px';
        tempContainer.style.paddingBottom = '40px';
        document.body.appendChild(tempContainer);

        // Convert all images to data URLs to avoid CORS/tainted canvas issues
        // Note: Images from apps.kubotausa.com don't have CORS headers, so conversion will fail
        // html2canvas will handle these images, but they may appear blank in the PDF
        const images = tempContainer.querySelectorAll('img');
        await Promise.all(
            Array.from(images).map(img => {
                if (img.src.startsWith('data:')) return;
            
                return new Promise(resolve => {
                    const newImg = new Image();
                    newImg.crossOrigin = 'anonymous';
                    newImg.src = img.src;
            
                    newImg.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = newImg.naturalWidth;
                    canvas.height = newImg.naturalHeight;
            
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(newImg, 0, 0);
            
                    try {
                        img.src = canvas.toDataURL('image/png');
                    } catch {
                        console.warn('Canvas tainted for image:', img.src);
                    }
                        resolve();
                    };
            
                    newImg.onerror = () => resolve();
                });
            })
        );
          

        // Wait a bit for images to load
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Use html2canvas to convert HTML to canvas with good quality
        // Note: Images from apps.kubotausa.com don't have CORS headers
        // html2canvas will attempt to load them, but they may appear blank if CORS blocks them
        const canvas = await html2canvas(tempContainer, {
            scale: 1.5,
            useCORS: true,
            allowTaint: false,
            width: 1000,
            windowWidth: 1000,
            logging: false,
            backgroundColor: '#ffffff',
            removeContainer: false,
            onclone: (clonedDoc) => {
                const style = clonedDoc.createElement('style');
                style.innerHTML = `
                  * {
                    font-family: Arial, Helvetica, sans-serif !important;
                    letter-spacing: normal !important;
                    word-spacing: normal !important;
                    line-height: 1.4 !important;
                    -webkit-font-smoothing: antialiased !important;
                    text-rendering: optimizeLegibility !important;
                  }
              
                  p, span, div, td, th, li {
                    white-space: normal !important;
                  }
                `;
                clonedDoc.head.appendChild(style);
            }
        });

        // Create PDF and add the image with pagination support
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const margin = 10; // Top and bottom margins
        const imgWidth = pageWidth - (margin * 2); // 190mm
        const availablePageHeight = pageHeight - (margin * 2); // 277mm
        
        // Calculate the full image height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Calculate how many pages we need
        const totalPages = Math.ceil(imgHeight / availablePageHeight);
        
        // Calculate pixels per mm (same ratio for width and height to maintain aspect ratio)
        const pixelsPerMm = canvas.width / imgWidth;
        // Calculate how many pixels fit on each page (no overlap to avoid duplication)
        const pixelsPerPage = availablePageHeight * pixelsPerMm;
        
        // Split the image across multiple pages
        for (let page = 0; page < totalPages; page++) {
            if (page > 0) {
                pdf.addPage();
            }
            
            // Calculate the source Y position in pixels (no overlap)
            const sourceY = pixelsPerPage * page;
            const sourceHeight = Math.min(
                pixelsPerPage,
                canvas.height - sourceY
            );
            
            // Create a temporary canvas for this page's portion
            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = canvas.width;
            pageCanvas.height = sourceHeight;
            const pageCtx = pageCanvas.getContext('2d');
            
            // Draw the portion of the original canvas onto the page canvas
            pageCtx.drawImage(
                canvas,
                0, sourceY, canvas.width, sourceHeight, // Source rectangle
                0, 0, canvas.width, sourceHeight // Destination rectangle
            );
            
            // Convert this page's canvas to image data
            // Use JPEG with quality 0.85 to balance file size and text quality
            // JPEG is much smaller than PNG while still maintaining good text quality
            const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.85);
            
            // Calculate the height of this page's image in mm
            // For all pages except the last, this should equal availablePageHeight
            const pageImgHeight = (sourceHeight * imgWidth) / canvas.width;
            
            // Add this page's image to the PDF
            // JPEG format reduces file size significantly compared to PNG
            pdf.addImage(pageImgData, 'JPEG', margin, margin, imgWidth, pageImgHeight);
        }

        // Get PDF as blob
        const pdfBlob = pdf.output('blob');
        
        // Automatically download the PDF for testing/verification
        // const downloadUrl = URL.createObjectURL(pdfBlob);
        // const downloadLink = document.createElement('a');
        // downloadLink.href = downloadUrl;
        // downloadLink.download = `quote_${json.BMKQuoteId}.pdf`;
        // document.body.appendChild(downloadLink);
        // downloadLink.click();
        // document.body.removeChild(downloadLink);
        // URL.revokeObjectURL(downloadUrl);

        // Create FormData with the PDF file
        const data = new FormData();
        data.append("quoteid", json.BMKQuoteId);
        data.append("file", pdfBlob, `quote_${json.BMKQuoteId}.pdf`);

        let fileResponse = await fetch(bmkservice.IKQSavePDF, {
            method: "POST",
            body: data
        });
        await fileResponse.json();
        
        // Clean up the temporary container
        document.body.removeChild(tempContainer);

        return json.BMKQuoteId;
    }

    const renderModal = () => {
        if (!modalType) return null;

        const commonProps = { onClose: closeModal };

        switch (modalType) {
            case 'disclaimer':
                return <DisclaimerModal {...commonProps} {...modalProps} />;
            case 'login':
                return <LoginModal {...commonProps} {...modalProps} />;
            case 'signup':
                return <SignupModal {...commonProps} {...modalProps} />;
            case 'profile':
                return <ProfileModal {...commonProps} {...modalProps} />;
            case 'forgotPassword':
                return <ForgotPasswordModal {...commonProps} {...modalProps} />;
            case 'saveBuild':
                return <SaveBuildModal {...commonProps} {...modalProps} />;
            case 'loadBuild':
                return <LoadBuildModal {...commonProps} {...modalProps} />;
            case 'share':
                return <ShareModal {...commonProps} {...modalProps} />;
            case 'shareByEmail':
                return <ShareByEmailModal {...commonProps} {...modalProps} />;
            case 'loading':
                return <LoadingModal {...commonProps} {...modalProps} />;
            case 'success':
                return <SuccessModal {...commonProps} {...modalProps} />;
            case 'error':
                return <ErrorModal {...commonProps} {...modalProps} />;
            case 'changePassword':
                return <ChangePasswordModal {...commonProps} {...modalProps} />;
            case 'custom':
                return modalProps.content || null;
            default:
                return null;
        }
    };

    return (
        <ModalContext.Provider value={{
            showDisclaimer, trySave, tryLoad, showShare, sendToIKQ,
            showLogin, showForgotPassword, showSignup, showProfile,
            show, closeModal
        }}>
            {modalType ? <Modal onClose={closeModal}>{renderModal()}</Modal> : null}
            {children}
        </ModalContext.Provider>
    )
}
